/**
 * Scan POS P6 -- one Sale tab's Sale Surface and its realtime receiver.
 *
 * LIFECYCLE
 *   start()      SURFACE_OPEN -> ACTIVE or PASSIVE (the Backend decides; a new
 *                tab NEVER takes over a live owner). Receiver state starts
 *                WAITING; the page says READY when it is ready.
 *   heartbeat    SURFACE_HEARTBEAT every 10 s while the surface is open. It is
 *                the ONLY thing that keeps the 35 s lease alive.
 *   ACTIVE       one-time REALTIME_TICKET_WEB -> WebSocket with the ticket as
 *                the only query value (never the login bearer) -> PING -> PONG
 *                proves the binding -> VERIFIED. Only a VERIFIED channel hands
 *                SCAN_EVENT frames to the receiver.
 *   PASSIVE      no ticket, no socket, no scans. transfer() is the only way in
 *                (explicit, generation-checked); it then opens a NEW ticket and
 *                socket, and leaves the receiver WAITING until the page says READY.
 *   stop()       timers + socket closed, SURFACE_RELEASE best effort.
 *
 * RECEIVER STATE
 *   setReceiverState() changes the LOCAL state synchronously FIRST, then tells
 *   the Backend (calls are serialised so they land in order). Opening the
 *   payment screen therefore can never race an in-flight scan: the local gate
 *   already says LOCKED before the modal exists.
 *
 * WEBSOCKET PING is a separate, slower timer that only proves the pipe and
 *   surfaces CLOSED reasons. It never renews the lease (P4 rule).
 *
 * CHANNEL LOSS (socket closed, CLOSED frame): the channel stops being VERIFIED
 *   at once -- no scan is taken on it -- and a bounded recovery re-reads
 *   SURFACE_STATUS and, only if this surface is still ACTIVE at the same
 *   generation, opens a NEW ticket + socket. A used ticket is never reused.
 *
 * Nothing here logs a ticket, a token, a QR or a frame.
 */
import {
    ReceiverState, ScanPosApi, SurfaceRole, SurfaceView, scanErrorCode, scanErrorStatus,
} from '../api/scan-pos';
import { Binding } from './receiver';

export const HEARTBEAT_MS = 10_000;
export const PING_MS = 25_000;
export const RECOVERY_DELAYS_MS = [1_000, 3_000, 8_000];

export type ChannelState = 'NONE' | 'CONNECTING' | 'VERIFIED' | 'RECOVERING' | 'FAILED';

export interface SurfaceSnapshot {
    available: boolean;
    surfaceId: string | null;
    role: SurfaceRole | null;
    generation: number;
    receiverState: ReceiverState;
    channel: ChannelState;
    /** Ready to take a scan right now: ACTIVE + VERIFIED + local READY. */
    ready: boolean;
    error: string | null;
}

interface SocketLike {
    readyState: number;
    send(data: string): void;
    close(code?: number): void;
    onopen: ((ev: any) => void) | null;
    onmessage: ((ev: any) => void) | null;
    onclose: ((ev: any) => void) | null;
    onerror: ((ev: any) => void) | null;
}

export interface SurfaceSessionDeps {
    api: Pick<ScanPosApi, 'surfaceOpen' | 'surfaceStatus' | 'surfaceHeartbeat' | 'surfaceStateSet'
        | 'surfaceTransfer' | 'surfaceRelease' | 'realtimeTicketWeb'>;
    realtimeUrl: string;
    WebSocketCtor: new (url: string) => SocketLike;
    onFrame(frame: any, binding: Binding): void;
    /** Fast Wireless Barcode Path: a BARCODE_SCAN on the current VERIFIED socket. */
    onFastFrame?(frame: any, binding: Binding): void;
    onChange(snapshot: SurfaceSnapshot): void;
    /** The Backend refused for lack of permission / session: stop integration. */
    onForbidden?(): void;
    /** This surface just became ACTIVE (open or transfer); the page decides READY. */
    onBecameActive?(): void;
    nonce?(): string;
}

const OPEN = 1;

export class SurfaceSession {
    private readonly deps: SurfaceSessionDeps;
    private surfaceId: string | null = null;
    private role: SurfaceRole | null = null;
    private generation = 0;
    private localState: ReceiverState = 'WAITING';
    /** What the page last asked for -- re-applied if the Backend drifted. */
    private wanted: ReceiverState = 'WAITING';
    private stateCallsInFlight = 0;
    private channel: ChannelState = 'NONE';
    private channelId = 0;
    private socket: SocketLike | null = null;
    private pingNonce: string | null = null;
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private pingTimer: ReturnType<typeof setInterval> | null = null;
    private recoveryTimer: ReturnType<typeof setTimeout> | null = null;
    private recoveryAttempt = 0;
    private stateChain: Promise<unknown> = Promise.resolve();
    private stopped = false;
    private available = true;
    private error: string | null = null;
    private nonceCounter = 0;

    constructor(deps: SurfaceSessionDeps) {
        this.deps = deps;
    }

    // ------------------------------------------------------------ reading
    snapshot(): SurfaceSnapshot {
        return {
            available: this.available,
            surfaceId: this.surfaceId,
            role: this.role,
            generation: this.generation,
            receiverState: this.localState,
            channel: this.channel,
            ready: this.role === 'ACTIVE' && this.channel === 'VERIFIED' && this.localState === 'READY',
            error: this.error,
        };
    }

    receiverState(): ReceiverState {
        return this.localState;
    }

    /** The binding scans may arrive on, or null when none is verified. */
    currentBinding(): Binding | null {
        if (this.stopped || this.role !== 'ACTIVE' || this.channel !== 'VERIFIED' || !this.surfaceId) return null;
        return { surfaceId: this.surfaceId, generation: this.generation, channel: this.channelId };
    }

    private emit() {
        if (!this.stopped) this.deps.onChange(this.snapshot());
    }

    // ------------------------------------------------------------ lifecycle
    async start(): Promise<void> {
        this.stopped = false;
        try {
            const view = await this.deps.api.surfaceOpen();
            this.localState = 'WAITING';
            this.wanted = 'WAITING';
            this.apply(view);
            this.error = null;
        } catch (error) {
            this.fail(error);
            return;
        }
        if (!this.heartbeatTimer) {
            this.heartbeatTimer = setInterval(() => { void this.heartbeat(); }, HEARTBEAT_MS);
        }
        this.emit();
        if (this.role === 'ACTIVE') {
            this.deps.onBecameActive?.();
            await this.connect();
        }
    }

    stop(release = true): void {
        const surfaceId = this.surfaceId;
        this.stopped = true;
        this.clearTimers();
        this.dropSocket();
        this.localState = 'WAITING';
        if (release && surfaceId) {
            this.deps.api.surfaceRelease(surfaceId).catch(() => undefined);
        }
    }

    private clearTimers() {
        if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
        if (this.pingTimer) clearInterval(this.pingTimer);
        if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
        this.heartbeatTimer = this.pingTimer = this.recoveryTimer = null;
    }

    private apply(view: SurfaceView) {
        const own = view?.surface;
        if (own) {
            this.surfaceId = own.surface_id;
            this.role = own.role;
        }
        this.generation = Number(view?.terminal?.generation ?? this.generation);
    }

    private fail(error: unknown) {
        const status = scanErrorStatus(error);
        if (status === 401 || status === 403) {
            this.available = false;
            this.error = 'FORBIDDEN';
            this.stop(false);
            this.deps.onChange(this.snapshot());
            this.deps.onForbidden?.();
            return;
        }
        this.error = scanErrorCode(error) || 'UNAVAILABLE';
        this.emit();
    }

    /** Called when the role / generation changed under us. */
    private reconcile(previousRole: SurfaceRole | null, previousGeneration: number) {
        if (this.role !== 'ACTIVE') {
            if (this.socket || this.channel !== 'NONE') this.dropSocket();
            if (this.role === 'CLOSED' || this.role === 'PASSIVE') this.localState = 'WAITING';
        } else if (previousRole !== 'ACTIVE' || previousGeneration !== this.generation) {
            this.dropSocket();
            this.localState = 'WAITING';
            this.deps.onBecameActive?.();
            void this.connect();
        }
        this.emit();
    }

    // ------------------------------------------------------------ heartbeat
    async heartbeat(): Promise<void> {
        if (this.stopped || !this.surfaceId) return;
        if (this.role === 'CLOSED') {
            // The old surface is gone (lease lapsed while asleep): open a new
            // one -- ACTIVE only if nobody else owns the terminal.
            this.surfaceId = null;
            await this.start();
            return;
        }
        const previousRole = this.role;
        const previousGeneration = this.generation;
        try {
            const view = await this.deps.api.surfaceHeartbeat(this.surfaceId, this.generation);
            if (this.stopped) return;
            this.apply(view);
            this.error = null;
            // Self-heal: a state call that failed (or was overtaken) leaves the
            // Backend on another state than the page asked for. Re-apply the
            // page's wish -- local first again, like every state change.
            const backendState = view?.surface?.receiver_state;
            if (this.role === 'ACTIVE' && previousRole === 'ACTIVE' && previousGeneration === this.generation
                && this.stateCallsInFlight === 0 && backendState && backendState !== this.wanted) {
                void this.setReceiverState(this.wanted);
            }
        } catch (error) {
            if (this.stopped) return;
            const code = scanErrorCode(error);
            if (code === 'STALE_GENERATION' || code === 'NOT_ACTIVE') {
                await this.refresh();
                return;
            }
            if (code === 'SURFACE_CLOSED' || code === 'SURFACE_NOT_FOUND') {
                this.role = 'CLOSED';
            } else {
                this.fail(error);
                return;
            }
        }
        this.reconcile(previousRole, previousGeneration);
    }

    /** SURFACE_STATUS: re-read the authority and reconcile. */
    async refresh(): Promise<SurfaceView | null> {
        if (this.stopped || !this.surfaceId) return null;
        const previousRole = this.role;
        const previousGeneration = this.generation;
        try {
            const view = await this.deps.api.surfaceStatus(this.surfaceId);
            if (this.stopped) return null;
            this.apply(view);
            this.reconcile(previousRole, previousGeneration);
            return view;
        } catch (error) {
            const code = scanErrorCode(error);
            if (code === 'SURFACE_NOT_FOUND' || code === 'SURFACE_CLOSED') {
                this.role = 'CLOSED';
                this.reconcile(previousRole, previousGeneration);
            } else {
                this.fail(error);
            }
            return null;
        }
    }

    // ------------------------------------------------------------ receiver state
    /**
     * LOCAL FIRST, synchronously; then the Backend (serialised). Returns the
     * Backend call so a caller may wait, but never needs to.
     */
    setReceiverState(state: ReceiverState): Promise<unknown> {
        this.localState = state;
        this.wanted = state;
        this.emit();
        if (this.role !== 'ACTIVE' || !this.surfaceId) return Promise.resolve();
        const surfaceId = this.surfaceId;
        const generation = this.generation;
        this.stateCallsInFlight += 1;
        this.stateChain = this.stateChain.then(async () => {
            if (this.stopped || this.surfaceId !== surfaceId || this.generation !== generation) return;
            try {
                await this.deps.api.surfaceStateSet(surfaceId, state, generation);
            } catch (error) {
                if (this.stopped) return;
                if (state === 'READY' && this.localState === 'READY') {
                    this.localState = 'WAITING';                 // READY not confirmed: stay safe
                    this.emit();
                }
                const code = scanErrorCode(error);
                if (code === 'STALE_GENERATION' || code === 'NOT_ACTIVE' || code === 'SURFACE_CLOSED') {
                    await this.refresh();
                } else {
                    this.fail(error);
                }
            }
        }).finally(() => { this.stateCallsInFlight -= 1; });
        return this.stateChain;
    }

    // ------------------------------------------------------------ transfer
    /** Explicit, generation-checked. Never automatic. */
    async transfer(): Promise<boolean> {
        if (this.stopped || !this.surfaceId || this.role !== 'PASSIVE') return false;
        const previousRole = this.role;
        const previousGeneration = this.generation;
        try {
            const view = await this.deps.api.surfaceTransfer(this.surfaceId, this.generation);
            this.apply(view);
            this.error = null;
        } catch (error) {
            const code = scanErrorCode(error);
            if (code === 'STALE_GENERATION') {
                this.error = 'STALE_GENERATION';
                await this.refresh();
            } else {
                this.fail(error);
            }
            return false;
        }
        this.reconcile(previousRole, previousGeneration);
        return (this.role as SurfaceRole | null) === 'ACTIVE';
    }

    // ------------------------------------------------------------ socket
    private dropSocket() {
        const socket = this.socket;
        this.socket = null;
        this.channelId += 1;                    // every frame from an older socket is now stale
        this.channel = 'NONE';
        this.pingNonce = null;
        if (this.pingTimer) clearInterval(this.pingTimer);
        this.pingTimer = null;
        if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
        this.recoveryTimer = null;
        if (socket) {
            socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
            try { socket.close(1000); } catch { /* already closed */ }
        }
    }

    private nextNonce(): string {
        if (this.deps.nonce) return this.deps.nonce();
        this.nonceCounter += 1;
        return `p6-${Date.now().toString(36)}-${this.nonceCounter}`;
    }

    private async connect(): Promise<void> {
        if (this.stopped || this.role !== 'ACTIVE' || !this.surfaceId) return;
        this.dropSocket();
        this.channel = 'CONNECTING';
        this.emit();
        const surfaceId = this.surfaceId;
        const generation = this.generation;
        let url: string;
        try {
            const issued = await this.deps.api.realtimeTicketWeb(surfaceId, generation);
            url = `${this.deps.realtimeUrl}?ticket=${encodeURIComponent(issued.ticket)}`;
        } catch (error) {
            const code = scanErrorCode(error);
            if (code === 'STALE_GENERATION' || code === 'NOT_ACTIVE' || code === 'SURFACE_CLOSED') {
                this.channel = 'NONE';
                await this.refresh();
            } else {
                this.lost();
                if (scanErrorStatus(error) === 403 || scanErrorStatus(error) === 401) this.fail(error);
            }
            return;
        }
        if (this.stopped || this.surfaceId !== surfaceId || this.generation !== generation || this.role !== 'ACTIVE') return;

        const myChannel = this.channelId;
        const socket = new this.deps.WebSocketCtor(url);
        this.socket = socket;
        const mine = () => this.socket === socket && this.channelId === myChannel && !this.stopped;

        socket.onopen = () => {
            if (!mine()) return;
            this.sendPing();
            this.pingTimer = setInterval(() => this.sendPing(), PING_MS);
        };
        socket.onmessage = (ev: any) => {
            if (!mine()) return;
            let frame: any;
            try { frame = JSON.parse(String(ev?.data)); } catch { return; }
            if (!frame || typeof frame !== 'object') return;
            if (frame.type === 'PONG') {
                if (this.channel !== 'VERIFIED' && frame.nonce === this.pingNonce) {
                    this.channel = 'VERIFIED';
                    this.recoveryAttempt = 0;
                    this.emit();
                }
            } else if (frame.type === 'CLOSED') {
                this.lost();                                   // authoritative: this socket is done
            } else if (frame.type === 'SCAN_EVENT') {
                this.deps.onFrame(frame, { surfaceId, generation, channel: myChannel });
            } else if (frame.type === 'BARCODE_SCAN') {
                // Fast path: only a VERIFIED channel may hand a barcode to the Cart.
                if (this.channel === 'VERIFIED' && this.deps.onFastFrame) {
                    this.deps.onFastFrame(frame, { surfaceId, generation, channel: myChannel });
                }
            }
            // TEST_EVENT and anything unknown: no action, ever.
        };
        socket.onclose = () => { if (mine()) this.lost(); };
        socket.onerror = () => { /* onclose follows */ };
    }

    /**
     * Fast path: BARCODE_ACK on the socket the scan arrived on -- only if that
     * socket is STILL the current, VERIFIED, open one for the same surface and
     * generation. One send, no retry; false when it could not be sent.
     * Carries the request_id and result only (no barcode, Cart, session,
     * surface or generation).
     */
    sendFastBarcodeAck(binding: Binding, requestId: string, result: string): boolean {
        const socket = this.socket;
        if (this.stopped || this.role !== 'ACTIVE' || this.channel !== 'VERIFIED' || !socket
            || socket.readyState !== OPEN || binding.channel !== this.channelId
            || binding.surfaceId !== this.surfaceId || binding.generation !== this.generation) {
            return false;
        }
        try {
            socket.send(JSON.stringify({ action: 'BARCODE_ACK', request_id: requestId, result }));
            return true;
        } catch {
            return false;
        }
    }

    private sendPing() {
        const socket = this.socket;
        if (!socket || socket.readyState !== OPEN) return;
        if (this.channel !== 'VERIFIED' || !this.pingNonce) this.pingNonce = this.nextNonce();
        try {
            socket.send(JSON.stringify({ action: 'PING', nonce: this.pingNonce }));
        } catch { /* onclose will follow */ }
    }

    /** The channel can no longer be trusted: stop taking scans, recover (bounded). */
    private lost() {
        this.dropSocket();
        if (this.stopped || this.role !== 'ACTIVE') {
            this.emit();
            return;
        }
        if (this.recoveryAttempt >= RECOVERY_DELAYS_MS.length) {
            this.channel = 'FAILED';
            this.emit();
            return;
        }
        this.channel = 'RECOVERING';
        this.emit();
        const delay = RECOVERY_DELAYS_MS[this.recoveryAttempt];
        this.recoveryAttempt += 1;
        this.recoveryTimer = setTimeout(() => { void this.recover(); }, delay);
    }

    /** Re-confirm the authority FIRST, then open a new ticket + socket. */
    private async recover(): Promise<void> {
        this.recoveryTimer = null;
        if (this.stopped || !this.surfaceId) return;
        const generation = this.generation;
        const view = await this.refresh();
        if (!view || this.stopped) return;
        if (this.role === 'ACTIVE' && this.generation === generation && this.channel !== 'CONNECTING'
            && this.channel !== 'VERIFIED') {
            await this.connect();
        }
    }

    /** Manual retry after FAILED (button). */
    async retryChannel(): Promise<void> {
        this.recoveryAttempt = 0;
        await this.recover();
    }
}
